<template>
  <div class="pair">
    <div class="card">
      <img class="logo" src="@/assets/Epoka.png" alt="Epoka">
      <h1 class="title">Pair this screen</h1>

      <template v-if="!paired">
        <p class="sub">
          Ask an administrator for a pairing code from
          <strong>Admin &rsaquo; Screens</strong>, then enter it here.
        </p>

        <!-- Uppercase, spaced for reading. The alphabet has no 0/O or 1/I,
             and the server strips spaces and dashes, so what somebody reads
             off a phone can be typed exactly as it looks. -->
        <input
          v-model="code"
          class="code"
          type="text"
          autocomplete="off"
          autocapitalize="characters"
          spellcheck="false"
          maxlength="12"
          placeholder="ABCD 2345"
          aria-label="Pairing code"
          :disabled="busy"
          @keyup.enter="submit"
        >

        <p v-if="error" class="err">{{ error }}</p>

        <button class="btn" :disabled="cleaned.length < 8 || busy" @click="submit">
          {{ busy ? 'Pairing…' : 'Pair' }}
        </button>
      </template>

      <template v-else>
        <p class="sub">
          Paired as <strong>{{ name }}</strong>. Opening the display.
        </p>
      </template>
    </div>
  </div>
</template>


<script setup>
import { ref, computed } from 'vue'
import { pairScreen } from '@/services/ScreenService'
import { setDisplayKey } from '@/auth/displayKey'

const code = ref('')
const error = ref('')
const busy = ref(false)
const paired = ref(false)
const name = ref('')

const cleaned = computed(() => code.value.replace(/[\s-]/g, '').toUpperCase())

async function submit() {
  if (cleaned.value.length < 8 || busy.value) return

  busy.value = true
  error.value = ''

  try {
    const result = await pairScreen(cleaned.value)

    // The only moment the key exists outside the server. Stored and never
    // shown: a person has no reason to see it and every reason not to.
    setDisplayKey(result.key, result.name)

    name.value = result.name
    paired.value = true

    // A full reload rather than a route push, so the auth state is rebuilt
    // from storage with the key in place. Otherwise MSAL's initialisation, which
    // ran before pairing, still thinks nobody is signed in.
    setTimeout(() => { window.location.href = '/display' }, 1200)
  } catch (err) {
    console.error(err)
    // The server says the same thing for wrong, expired and used codes, on
    // purpose. This adds only what the person can act on.
    error.value = (err.message ?? 'Could not pair') + '. Ask for a fresh code.'
  } finally {
    busy.value = false
  }
}
</script>


<style scoped>
.pair {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f7f9fb;
  padding: 24px;
  text-align: center;
}

.card {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border: 1px solid #dfe4e9;
  border-radius: 16px;
  padding: 40px 32px;
}

.logo {
  height: 28px;
  margin-bottom: 24px;
}

.title {
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #003c6c;
  margin: 0 0 10px;
}

.sub {
  font-size: 14.5px;
  line-height: 1.55;
  color: #64748b;
  margin: 0 0 24px;
}

.code {
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 28px;
  letter-spacing: 0.18em;
  text-align: center;
  text-transform: uppercase;
  width: 100%;
  height: 64px;
  border: 2px solid #dfe4e9;
  border-radius: 12px;
  background: #fff;
  color: #003c6c;
  outline: none;
  box-sizing: border-box;
}

.code:focus { border-color: #003c6c; }

.err {
  font-size: 14px;
  color: #b3261e;
  margin: 12px 0 0;
}

.btn {
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  width: 100%;
  height: 50px;
  margin-top: 20px;
  border: none;
  border-radius: 12px;
  background: #003c6c;
  color: #fff;
  cursor: pointer;
}

.btn:hover:not(:disabled) { background: #00548f; }
.btn:disabled { opacity: 0.4; cursor: default; }
</style>
